import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { getApp } from 'firebase/app';
import { getMessaging, getToken, isSupported } from 'firebase/messaging';

import config from '../../envConfig';
import '../../shared/firebase/firebase-client';
import {
  type PushDevicePlatform,
  getPushDevicePlatform,
} from './push-platform';

function isIosBrowser(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }
  const userAgent = navigator.userAgent || '';
  const isAppleMobile = /iPad|iPhone|iPod/.test(userAgent);
  const isIpadOsDesktopMode =
    navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  return isAppleMobile || isIpadOsDesktopMode;
}

function isRunningInStandaloneMode(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    nav.standalone === true
  );
}

export function canUseWebPushInCurrentContext(): boolean {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return false;
  }
  if (isIosBrowser() && !isRunningInStandaloneMode()) {
    return false;
  }
  return true;
}

async function getWebFcmTokenAfterPermission(): Promise<string | null> {
  if (!canUseWebPushInCurrentContext()) {
    return null;
  }
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    return null;
  }
  const supported = await isSupported();
  if (!supported) {
    return null;
  }
  const vapidKey = config.firebaseWebPushVapidKey;
  if (!vapidKey || !vapidKey.trim()) {
    return null;
  }
  try {
    const messaging = getMessaging(getApp());
    const token = await getToken(messaging, { vapidKey: vapidKey.trim() });
    return token || null;
  } catch {
    return null;
  }
}

async function getWebFcmTokenIfAlreadyGranted(): Promise<string | null> {
  if (!canUseWebPushInCurrentContext()) {
    return null;
  }
  if (Notification.permission !== 'granted') {
    return null;
  }
  const supported = await isSupported();
  if (!supported) {
    return null;
  }
  const vapidKey = config.firebaseWebPushVapidKey;
  if (!vapidKey || !vapidKey.trim()) {
    return null;
  }
  try {
    const messaging = getMessaging(getApp());
    const token = await getToken(messaging, { vapidKey: vapidKey.trim() });
    return token || null;
  } catch {
    return null;
  }
}

async function ensureNativePushPermission(): Promise<boolean> {
  let perm = await PushNotifications.checkPermissions();
  if (perm.receive !== 'granted') {
    perm = await PushNotifications.requestPermissions();
  }
  return perm.receive === 'granted';
}

function waitForNativePushRegistrationToken(): Promise<string | null> {
  return new Promise((resolve) => {
    let settled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let regH: { remove: () => Promise<void> } | null = null;
    let errH: { remove: () => Promise<void> } | null = null;

    const finish = (value: string | null) => {
      if (settled) return;
      settled = true;
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
      void regH?.remove();
      void errH?.remove();
      resolve(value);
    };

    void (async () => {
      regH = await PushNotifications.addListener('registration', (ev) => {
        finish(ev.value?.trim() || null);
      });
      errH = await PushNotifications.addListener('registrationError', () => {
        finish(null);
      });
      timeoutId = setTimeout(() => finish(null), 20000);
      await PushNotifications.register();
    })();
  });
}

export async function obtainFcmTokenWithPermissionRequest(): Promise<{
  fcmToken: string;
  platform: PushDevicePlatform;
} | null> {
  const platform = getPushDevicePlatform();
  if (Capacitor.isNativePlatform()) {
    const ok = await ensureNativePushPermission();
    if (!ok) {
      return null;
    }
    const token = await waitForNativePushRegistrationToken();
    if (!token) {
      return null;
    }
    return { fcmToken: token, platform };
  }
  const token = await getWebFcmTokenAfterPermission();
  if (!token) {
    return null;
  }
  return { fcmToken: token, platform: 'web' };
}

export async function obtainFcmTokenSilentIfPermitted(): Promise<{
  fcmToken: string;
  platform: PushDevicePlatform;
} | null> {
  const platform = getPushDevicePlatform();
  if (Capacitor.isNativePlatform()) {
    const perm = await PushNotifications.checkPermissions();
    if (perm.receive !== 'granted') {
      return null;
    }
    const token = await waitForNativePushRegistrationToken();
    if (!token) {
      return null;
    }
    return { fcmToken: token, platform };
  }
  const token = await getWebFcmTokenIfAlreadyGranted();
  if (!token) {
    return null;
  }
  return { fcmToken: token, platform: 'web' };
}
